import React, { useEffect, useState } from "react";
import { AdminApprovalGroup, AdminUser, IActionHandler, AdminUserBase, OrgInfo, Sort, SubsumeRequest, SortDirection, UserProfile } from "@preveil-api";
import {
  useAppDispatch, useGetOrgInfo, Account, AdminErrorMessages, ApprovalGroup, Message, MessageHandlerDisplayType, sortBy, SORT_DIRECTION, OrganizationInformation,
  getOppositeSortDirection, GlobalErrorMessages, mapUserToAdminUser, MessageAnchors, useAppSelector, AdminSuccessMessages, MessageToastTypes, useDeleteOrgUserMutation, PendingGroupUpdatesByUser, UserRequest,
  RequestTypes, OrgUserStatus, UserResultsMode, AdminWarningMessages, useDeleteOrgUserInviteMutation, PendingAgStatus
} from "src/common";
import { CoverTemplate, PageHeader } from "src/components";
import { SidePanel, UsersListView, UsersToolbar } from ".";
import { adminActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  org_info: OrgInfo
}

function AdminUsersComponent(props: AllProps) {
  const { current_account, org_info } = props;
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state: RootState) => state.admin.selected);
  const [is_loading, setIsLoading] = useState<boolean>(true);
  const [filter_param, setFilterParam] = useState<string>("");
  const [users_list, setUsersList] = useState<AdminUser[]>([]);
  const [filtered_list, setFilteredList] = useState<AdminUser[]>([]);
  const [admin_users_count, setAdminUsersCount] = useState<number>(0);
  const [allow_add_devices, setAllowAddDevices] = useState<boolean>();
  const [add_device_overrides, setAddDeviceOverrides] = useState<string[]>([]);
  const [current_selected_user, setCurrentSelectedUser] = useState<AdminUser>();
  const [recovery_groups, setRecoveryGroups] = useState<AdminApprovalGroup[]>([]);
  const [admin_approval_group, setAdminApprovalGroup] = useState<AdminApprovalGroup>();
  const [sort, setSort] = useState<Sort<keyof AdminUser>>({ field: "user_id", direction: SORT_DIRECTION.ascending });
  const [deleteOrgUser] = useDeleteOrgUserMutation();
  const [deleteOrgUserInvite] = useDeleteOrgUserInviteMutation();
  const { data, error, refresh } = useGetOrgInfo(current_account, org_info, UserResultsMode.lightweight, PendingAgStatus.include);

  // Description: On initial load - call the use get org info hook to get the approval groups and requests.
  useEffect(() => {
    fetchOrgData();
    return () => {
      dispatch(adminActions.setSelected([]));
    };
  }, []);

  // Description: handle data and error from getOrgInfo hook.
  useEffect(() => {
    if (!!data) {
      setOrgData(data);
      setIsLoading(false);
    }

    if (!!error) {
      dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_fetching_org_info, MessageHandlerDisplayType.toastr), error));
      setIsLoading(false);
    }
  }, [data, error]);

  // Description: Fetch the org data.
  function fetchOrgData() {
    setIsLoading(true);
    return refresh({
      history: false,
      status: "pending",
      hideExpired: true,
      limit: 200,
    });
  };

  // Description: Parses the data from getOrgInfo hook and sets various states.
  function setOrgData(data: OrganizationInformation) {
    let total_admin_users = 0;
    let no_recovery_or_unclaimed = false;
    const { approval_groups, org_info } = data;
    const { roled_approval_groups } = org_info;
    if (!!roled_approval_groups && !!roled_approval_groups.admin_approval_group) {
      const approval_group = approval_groups.find((group: ApprovalGroup) => group.approval_group_id === roled_approval_groups.admin_approval_group?.group_id);
      setAdminApprovalGroup(approval_group);
    } else {
      setAdminApprovalGroup(undefined);
    }
    setAllowAddDevices(org_info.allow_add_devices);
    setAddDeviceOverrides(org_info.add_device_overrides ?? []);
    const recovery_groups = approval_groups.filter((group: ApprovalGroup) => !group.is_deleted);
    setRecoveryGroups(recovery_groups);
    const users = data.lightweight_org_users.map((raw_user: AdminUserBase) => {
      const user = mapUserToAdminUser(raw_user);
      const recovery_group = recovery_groups.find((rg: ApprovalGroup) => rg.approval_group_id === user.recovery_group_id);
      const pending_ag_status = data.pending_ag_status_by_user[raw_user.canonical_user_id.toLowerCase()];
      no_recovery_or_unclaimed = no_recovery_or_unclaimed || !!pending_ag_status?.recovery_group_pending || !!pending_ag_status?.pending_recovery_group_id || !raw_user.recovery_group_id || raw_user.claimed_at === null;
      const updates = data.pending_group_updates[user.user_id.toLowerCase()];
      const updates_pending = updates ? updates.length : 0;
      const user_pending_delete_total = updates_pending > 0 ? updates.filter((request: PendingGroupUpdatesByUser[string][number]) => request.request_type === RequestTypes.delete_user).length : undefined;
      const user_pending_role_change_total = updates_pending > 0 ? updates.filter((request: PendingGroupUpdatesByUser[string][number]) => request.request_type === RequestTypes.change_admin_status).length : undefined;
      const user_pending_group_change_total = updates_pending > 0 ? updates.filter((request: PendingGroupUpdatesByUser[string][number]) => request.request_type === RequestTypes.member_rekey_and_set_approval_group).length : undefined;
      const org_user = { ...user, recovery_group, ...pending_ag_status, updates_pending, user_pending_delete_total, user_pending_role_change_total, user_pending_group_change_total };
      // we want to keep count of how many admins are within the org.
      // if there is only one admin in the org, we need to disable the change role button.
      total_admin_users = org_user.role === "admin" ? total_admin_users + 1 : total_admin_users;
      return org_user;
    });
    const users_list = [...users, ...parseOrgRequests(data.org_requests)];
    const sorted_users_list = sortBy(users_list, { field: "user_id", direction: SORT_DIRECTION.ascending });
    setAdminUsersCount(total_admin_users);
    setUsersList(sorted_users_list);
    setFilteredList(sorted_users_list);
    // Description: useGetOrgInfo skips the actions_pending dispatch in lightweight mode, so compute it here instead.
    dispatch(adminActions.setActionsPending(Object.keys(data.pending_group_updates).length > 0 || no_recovery_or_unclaimed));
  }

  // Description: parses through the org requests and returns the subsume requests as invited users.
  function parseOrgRequests(requests: UserRequest[]): AdminUser[] {
    const invited_users: AdminUser[] = [];
    requests.forEach((request: UserRequest) => {
      if (request.type === RequestTypes.subsume_account && request.for_users.length > 0) {
        // Iterate over the subsume request users since those are not part of the org users (not present in the org_users array).
        const { for_users, is_admin, metadata, request_id } = request;
        const { department } = metadata as SubsumeRequest;
        const role = is_admin ? "admin" : "standard";
        const status = request.status === OrgUserStatus.pending ? OrgUserStatus.invited : request.status;
        const display_name = for_users[0].name as string;
        const user_id = for_users[0].address;

        invited_users.push({ status, display_name, user_id, department, role, devices: [], subsume_request_id: request_id });
      }
    });
    return invited_users;
  }

  // Description: Handles the request to remove selected users from the organization.
  async function removeSelectedUsersFromOrg(users: AdminUser[]) {
    if (_.isArray(users)) {
      const params = {
        user_id: current_account.user_id,
        org_id: org_info.org_id,
      };
      const failed_users_request: string[] = [];
      let invited_users: number = 0;
      // If there are multiple users selected, we need to make a request for each user.
      let message = "";
      await Promise.all(users.map(async (user: AdminUser) => {
        const active_ag_member = recovery_groups.some((group: AdminApprovalGroup) => group.approvers.some((member: UserProfile) => member.address === user.user_id));
        if (active_ag_member) {
          message = AdminErrorMessages.error_request_delete_active_AG_member;
          failed_users_request.push(user.user_id);
        } else {
          if (user.status === OrgUserStatus.invited && !!user.subsume_request_id) {
            await deleteOrgUserInvite({ ...params, request_id: user.subsume_request_id }).unwrap()
              .then(() => {
                invited_users++;
                setUsersList(users_list.filter((org_user: AdminUser) => org_user.user_id !== user.user_id));
              }).catch(() => {
                failed_users_request.push(user.user_id);
              });
          } else {
            await deleteOrgUser({ ...params, user_to_be_deleted_id: user.user_id }).unwrap()
              .then(() => {
                setUsersList(users_list.filter((org_user: AdminUser) => org_user.user_id !== user.user_id));
              }).catch(() => {
                failed_users_request.push(user.user_id);
              });
          }
        }
      }));

      const success_deleted = users.length - failed_users_request.length;
      if (success_deleted > 0) {
        if (success_deleted === invited_users) {
          const message = AdminSuccessMessages.success_request_delete_invited_users.replace(MessageAnchors.message_content, `${invited_users} user invite${invited_users > 1 ? "s" : ""}`);
          dispatch(uiActions.handleSetMessage(new Message(message, MessageHandlerDisplayType.toastr, MessageToastTypes.success)));
        } else if (admin_approval_group) {
          dispatch(uiActions.handleSetMessage(new Message(AdminWarningMessages.warning_approval_delete_user.replace(MessageAnchors.prev_name, admin_approval_group.name).replace(MessageAnchors.message_content, `${success_deleted - invited_users} user${(success_deleted - invited_users) > 1 ? "s" : ""}`), MessageHandlerDisplayType.warning, MessageToastTypes.warning)));
        } else {
          const users_message_template = success_deleted === users.length ? `${users.length} users` : `${success_deleted} out of ${users.length} users`;
          const success_message = AdminSuccessMessages.success_request_delete_users.replace(MessageAnchors.message_content, `${users_message_template}`);
          const display_type = success_deleted === users.length ? MessageToastTypes.success : MessageToastTypes.warning;
          dispatch(uiActions.handleSetMessage(new Message(success_message, MessageHandlerDisplayType.toastr, display_type)));
        }
      } else {
        message = message || AdminErrorMessages.error_request_delete_user.replace(MessageAnchors.message_content, `${users.length} user${users.length > 1 ? "s" : ""} from organization.`);
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.toastr)));
      }
      dispatch(adminActions.setSelected([]));
      fetchOrgData();
    }
  }

  function handleSortRoleAndRG(column: "role" | "recovery_group", list: AdminUser[], direction: SortDirection): AdminUser[] {
    // For recovery group we need to sort by the nested "name" (recovery_group.name) property.
    // If the user has a pending recovery group change or pending invite, we want to sort those at the top when sorting ascending
    // or the bottom when sorting descending.
    // For role we want to sort by role but only for active users (standard/admin), and keep the pending and invited users at the top or bottom depending on sort direction.
    let sorted_list: AdminUser[] = list;
    if (column === "recovery_group") {
      sorted_list = _.orderBy(
        list,
        [(user: AdminUser) => user.recovery_group?.name?.toLowerCase() && !user.pending_recovery_group_id ? user.recovery_group.name.toLowerCase() : ""],
        [direction]
      );
    } else if ( column === "role" ) {
      sorted_list = _.orderBy(
        list,
        [(user: AdminUser) => user.status === "active" ? user.role.toLowerCase() : ""],
        [direction]
      );
    }
    return sorted_list;
  }

  const AdminUsersRequests = {
    handleSetSort: (params: "display_name" | "user_id" | "department" | "recovery_group" | "role") => {
      const direction = sort.field === params ? getOppositeSortDirection(sort.direction) : SORT_DIRECTION.ascending;
      const sorted_requests = (params === "role" || params === "recovery_group") ? handleSortRoleAndRG(params, filtered_list, direction) : sortBy(filtered_list, { field: params, direction });
      setSort({ field: params, direction });
      setFilteredList(sorted_requests);
    },
    handleGetRefreshRequest: () => {
      fetchOrgData();
      setSort({ field: "user_id", direction: SORT_DIRECTION.ascending });
    },
    handleSearchParam: (param?: string) => {
      if (!!param) {
        setFilterParam(param.toLowerCase());
        const filtered = users_list.filter((user: AdminUser) => {
          return Object.values(user).some(field =>
            _.isString(field) && field.toLowerCase().includes(param.toLowerCase())
          );
        });
        setFilteredList(filtered);
      } else {
        setFilterParam("");
        setFilteredList(users_list);
      }
    },
    handleShowSideNavMenu: (params?: { user: AdminUser }) => {
      const user = !!params ? params.user : undefined;
      setCurrentSelectedUser(user);
    },
    handleDelete: () => {
      const users_to_delete = users_list.filter((user) => selected.includes(user.user_id));
      removeSelectedUsersFromOrg(users_to_delete);
    },
    handleSelect: (params: { user?: AdminUser, selected: boolean }) => {
      let new_selected = selected.slice();
      const _id = !!params.user ? params.user.user_id : null;
      if (!!_id) { // Note: individual check
        if (params.selected) {
          !_.find(new_selected, (_selected: string) => (_selected === _id)) &&
            new_selected.push(_id);
        } else {
          const index = _.findIndex(new_selected, (_selected: string) => (_selected === _id));
          index >= 0 && (new_selected.splice(index, 1));
        }
      } else {
        // NOTE: Filter out locked files as those entries should not be selected, deleted, etc when they are locked.
        new_selected = params.selected ? users_list.map((user) => user.user_id) : [];
      }
      dispatch(adminActions.setSelected(new_selected));
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      const { message, stack } = params;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), stack));
    }
  };

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((AdminUsersRequests as any)[callback] instanceof Function) {
      (AdminUsersRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      AdminUsersRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  return <CoverTemplate className="admin-component">
    <PageHeader>
      <h1>Users</h1>
      <UsersToolbar current_account={current_account} is_loading={is_loading} users_list={users_list} handleAction={handlePageActions} filter_param={filter_param} recovery_groups={recovery_groups} />
    </PageHeader>
    <div id="outer-scope" className="cover-content">
      <UsersListView sort={sort} users_list={filtered_list} is_loading={is_loading} selected={selected} handleAction={handlePageActions} />
    </div>
    {!!current_selected_user && (
      <SidePanel
        handleAction={handlePageActions}
        current_account={current_account}
        current_selected_user={current_selected_user}
        allow_add_devices={allow_add_devices}
        add_device_overrides={add_device_overrides}
        recovery_groups={recovery_groups}
        total_admin_users={admin_users_count}
        users_list={users_list}
        admin_approval_group={admin_approval_group}
        org_info={org_info}
      ></SidePanel>
    )}
  </CoverTemplate>;
}

export default React.memo(AdminUsersComponent);
